import backend from './backend'
import { withAuthHeaders, withAuthMAModeOnly } from 'app/auth/appTokenQueue'
import { accessTokenToAuthorizationHeaders } from 'app/auth/authUtils'
import * as R from 'ramda'
import store from 'app/store'
import {
  ORDINARY_GROUP,
  HOMELESS_GROUP,
  EXTERNAL_GROUP,
} from 'commons/groupLogic'
import { notificationTranslationMapper } from 'commons/notification'

const parseResponse = (response) => {
  return response.data
}

// (() -> AppState) -> (Int -> a) -> a
const createWithPlaceId = (getState) => (f) =>
  f(() =>
    R.view(R.lensPath(['auth', 'user', 'selectedPlace', 'id']), getState()),
  )

const withPlaceId = createWithPlaceId(store.getState)
const recentSubscriberStatus = '3,4,5'
const CONVERSATION_FETCH_SIZE = 20

/*PLACE_INVITATION_INVITED = 1;
PLACE_INVITATION_PENDING_REQUEST  = 2;
PLACE_INVITATION_ACCEPT_REQUEST  = 3;
PLACE_INVITATION_ACCEPT_INVITATION  = 4;
PLACE_INVITATION_PUBLIC_SUBSCRIBE     = 5;
PLACE_INVITATION_DECLINED = 6;
PLACE_INVITATION_IGNORED = 7;
PLACE_INVITATION_REMOVE_SUBSCRIPTION = 8;
PLACE_INVITATION_UNSUBSCRIBED = 9;*/
const filtersSubscriber = '3,4,5'

export default {
  healtCheck: withAuthMAModeOnly(
    (authHeaders) => () => backend.healtCheck(authHeaders).then(parseResponse),
  ),

  confirmChangingPlaceEmail: ({ token }) =>
    backend.confirmChangingPlaceEmail({ token }).then(parseResponse),

  activatePlace: (token) => backend.activatePlace(token).then(parseResponse),

  checkEmail: ({ email, isExistence }) =>
    backend.checkEmail({ email, isExistence }).then(parseResponse),

  register: (data) => backend.register(data).then(parseResponse),

  postCuratedPlaceSingUp: (data) =>
    backend.postCuratedPlaceSingUp(data).then(parseResponse),

  login: withAuthMAModeOnly(
    (authHeaders) =>
      ({ username, password, isKeepMeSignedIn }) =>
        backend
          .login({ username, password, isKeepMeSignedIn }, authHeaders)
          .then(parseResponse),
  ),

  logout: withAuthHeaders(
    (authHeaders) => () => backend.logout(authHeaders).then(parseResponse),
  ),

  resetPassword: ({ code, password }) =>
    backend.resetPassword({ code, password }).then(parseResponse),

  forgot: (data) => backend.forgot(data).then(parseResponse),

  refreshToken: withAuthMAModeOnly(
    (authHeaders) =>
      ({ refreshToken }) =>
        backend
          .refreshToken({ token: refreshToken }, authHeaders)
          .then(parseResponse),
  ),
  refreshTokenManager: withAuthMAModeOnly(
    (authHeaders) => () =>
      backend.refreshTokenManager({}, authHeaders).then(parseResponse),
  ),

  loginWithSSO: withAuthMAModeOnly(
    (authHeaders) =>
      ({ token }) =>
        backend.loginWithSSO({ token }, authHeaders).then(parseResponse),
  ),

  generateIntegrationAPIToken: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .generateIntegrationAPIToken(getPlaceId(), authHeaders)
          .then(parseResponse),
    ),
  ),

  me: (accessToken) =>
    backend
      .me(null, accessTokenToAuthorizationHeaders(accessToken))
      .then(parseResponse),

  getAdminProfile: withAuthHeaders(
    (authHeaders) => () => backend.me(null, authHeaders).then(parseResponse),
  ),

  getBillingAddress: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getBillingAddress(getPlaceId(), authHeaders)
          .then(parseResponse),
    ),
  ),

  getBillingHistory: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ page, limit }) =>
          backend
            .getBillingHistory(
              { placeId: getPlaceId(), page, limit },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  getBillingToken: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ isCustomerIncluded }) =>
          backend
            .getBillingToken(
              { placeId: getPlaceId(), isCustomerIncluded },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  purchasePlan: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .purchasePlan({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  rechargePlan: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .rechargePlan({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  cancelPlan: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend.cancelPlan(getPlaceId(), authHeaders).then(parseResponse),
    ),
  ),

  getPaymentMethod: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend.getPaymentMethod(getPlaceId(), authHeaders).then(parseResponse),
    ),
  ),

  setDefaultPaymentMethod: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (token) =>
        backend
          .setDefaultPaymentMethod(
            { placeId: getPlaceId(), token },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  createPaymentMethod: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .createPaymentMethod({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  deletePaymentMethod: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .deletePaymentMethod({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  verifyPaymentMethod: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .verifyPaymentMethod({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  updatePaymentMethod: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .updatePaymentMethod({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  getUsageHistory: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ page, limit }) =>
          backend
            .getUsageHistory(
              { placeId: getPlaceId(), page, limit },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  getBillingCustomer: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getBillingCustomer(getPlaceId(), authHeaders)
          .then(parseResponse),
    ),
  ),

  updateBillingAddress: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .updateBillingAddress({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  createBillingShippingAddress: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .createBillingShippingAddress(
            { placeId: getPlaceId(), data },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  updateBillingShippingAddress: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .updateBillingShippingAddress(
            { placeId: getPlaceId(), data },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  getBillingShippingAddress: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .getBillingShippingAddress(getPlaceId(), authHeaders)
          .then(parseResponse),
    ),
  ),

  resentPlaceVerificationEmail: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .resentPlaceVerificationEmail(authHeaders, getPlaceId())
          .then(parseResponse),
    ),
  ),

  cancelPlaceVerificationEmail: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .cancelPlaceVerificationEmail(authHeaders, getPlaceId())
          .then(parseResponse),
    ),
  ),

  getPlaceById: (accessToken) => (id) =>
    backend
      .getPlaceById(id, accessTokenToAuthorizationHeaders(accessToken))
      .then(parseResponse),

  categories: ({ page, limit }) =>
    backend.categories({ page, limit }).then(parseResponse),

  getPlaceByIdAuth: withAuthHeaders(
    (authHeaders) => (id) =>
      backend.getPlaceById(id, authHeaders).then(parseResponse),
  ),

  saveProfileInfo: withAuthHeaders(
    (authHeaders) =>
      ({ placeId, data }) =>
        backend
          .saveProfileInfo({ placeId, data }, authHeaders)
          .then(parseResponse),
  ),

  savePlacePublic: withAuthHeaders(
    (authHeaders) =>
      ({ placeId, data }) =>
        backend
          .savePlacePublic({ placeId, data }, authHeaders)
          .then(parseResponse),
  ),

  savePlaceProfileEntity: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ data }) =>
          backend
            .savePlaceProfileEntity(
              { placeId: getPlaceId(), data },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  uploadProfilePhoto: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ data }) =>
          backend
            .uploadProfilePhoto({ placeId: getPlaceId(), data }, authHeaders)
            .then(parseResponse),
    ),
  ),
  uploadLineProfile: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ data }) =>
          backend
            .uploadLineProfile({ placeId: getPlaceId(), data }, authHeaders)
            .then(parseResponse),
    ),
  ),

  groups: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({
          page,
          types = [ORDINARY_GROUP, HOMELESS_GROUP, EXTERNAL_GROUP],
          ...props
        }) =>
          backend
            .groups(
              {
                placeId: getPlaceId(),
                page,
                type: types.join(','),
                ...props,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  getDeals: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ ...props }) =>
          backend
            .getDeals(
              {
                placeId: getPlaceId(),
                ...props,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  groupsWithTags: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({
          page,
          // types = [ORDINARY_GROUP, HOMELESS_GROUP, EXTERNAL_GROUP],
          ...props
        }) =>
          backend
            .groupsWithTags(
              {
                placeId: getPlaceId(),
                // type: types.join(','),
                ...props,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  groupsWithTagsWithOutType: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ ...props }) =>
          backend
            .groupsWithTags(
              {
                placeId: getPlaceId(),
                ...props,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  getGroupWithSubscriber: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ type, status = null, channels = null, scope = 1 }) =>
          backend
            .getGroupWithSubscriber(
              { placeId: getPlaceId(), type, status, channels, scope },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  searchRecipient: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (keyword, scope) =>
        backend
          .searchRecipient(
            { placeId: getPlaceId(), keyword, scope },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  initialEmail: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getInitialEmail({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),

  initialSms: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getInitialSms({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),

  updateInitialSms: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend.updateInitialSms({ placeId: getPlaceId(), data }, authHeaders),
    ),
  ),

  publishInitialEmail: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend.publishInitialEmail(
          { placeId: getPlaceId(), ...data },
          authHeaders,
        ),
    ),
  ),
  publishInitialSms: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend.publishInitialSms(
          { placeId: getPlaceId(), ...data },
          authHeaders,
        ),
    ),
  ),

  subscribers: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({
          page,
          limit,
          sort,
          keyword = null,
          status = null,
          tag = null,
          subgroups,
          groups,
          badContact,
          membershipCardIds,
          notificationMethods,
        }) =>
          backend
            .subscribers(
              {
                placeId: getPlaceId(),
                page,
                limit,
                sort,
                keyword,
                status,
                tag,
                subgroups,
                groups,
                badContact,
                membershipCardIds,
                notificationMethods,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  subscribersV2: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        (
          {
            page,
            limit,
            sort,
            keyword = null,
            status = null,
            tag = null,
            subgroups,
            groups,
            badContact,
            membershipCardIds,
            notificationMethods,
            source,
            zipCode,
          },
          options = {},
        ) =>
          backend
            .subscribersV2(
              {
                placeId: getPlaceId(),
                page,
                limit,
                sort,
                keyword,
                status,
                tag,
                subgroups,
                groups,
                badContact,
                membershipCardIds,
                notificationMethods,
                source,
                zipCode,
              },
              authHeaders,
              options,
            )
            .then(parseResponse),
    ),
  ),
  subscriberNotInTags: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ page, limit, sort, keyword = null, tagId }, options = {}) =>
          backend
            .subscriberNotInTags(
              {
                placeId: getPlaceId(),
                tagId,
                page,
                limit,
                sort,
                keyword,
              },
              authHeaders,
              options,
            )
            .then(parseResponse),
    ),
  ),

  getSubscriberForGroup: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        (
          { page, limit, sort, keyword = null, groupId, tagIds = undefined },
          options = {},
        ) =>
          backend
            .groupSubscriberV2(
              {
                placeId: getPlaceId(),
                page,
                limit,
                sort,
                keyword,
                groupId,
                tagIds,
              },
              authHeaders,
              options,
            )
            .then(parseResponse),
    ),
  ),

  subscribersCountForStatuses: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .subscribersCountForStatuses(
            {
              placeId: getPlaceId(),
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  //if you want to get total of subscribers you can use filtersSubscriber (status is 3,4,5)
  subscribersTotal: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subgroups, subscriber, notificationMethods }) =>
          backend
            .subscribersTotal(
              {
                placeId: getPlaceId(),
                subgroups,
                subscriber,
                status: filtersSubscriber,
                notificationMethods,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  recentSubscribers: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ page, limit }) =>
          backend
            .recentSubscribers(
              {
                placeId: getPlaceId(),
                page,
                limit,
                status: recentSubscriberStatus,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  teamMembers: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ page, limit, sort, keyword }) =>
          backend
            .teamMembers(
              { placeId: getPlaceId(), page, limit, sort, keyword },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  teamMemberById: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (id) =>
        backend
          .teamMemberById(
            { placeId: getPlaceId(), teamMemberId: id },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  getPermissions: withAuthHeaders(
    (authHeaders) => (requestedData) =>
      backend.getPermissions(authHeaders).then(parseResponse),
  ),

  getRoles: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ page, limit, sort }) =>
          backend
            .getRoles({ placeId: getPlaceId(), page, limit, sort }, authHeaders)
            .then(parseResponse),
    ),
  ),

  createRole: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .createRole({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  deleteRoles: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (ids) =>
        backend
          .deleteRoles({ placeId: getPlaceId(), ids }, authHeaders)
          .then(parseResponse),
    ),
  ),

  updateRole: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .updateRole({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  createTeamMember: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (requestedData) =>
        backend
          .createTeamMember(
            { placeId: getPlaceId(), ...requestedData },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  updateTeamMember: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (requestedData) =>
        backend
          .updateTeamMember(
            { placeId: getPlaceId(), ...requestedData },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  searchPlace: withAuthHeaders(
    (authHeaders) =>
      ({ keywords }) =>
        backend.searchPlace({ keywords }, authHeaders).then(parseResponse),
  ),

  getNotifications: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .getNotifications({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse)
          .then(
            R.over(
              R.lensProp('data'),
              R.map(notificationTranslationMapper.fromAPI),
            ),
          ),
    ),
  ),

  getAllPlaceLanguage: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ isAllPlaceLanguage = true }) =>
          backend
            .placeLanguage(
              {
                placeId: getPlaceId(),
                status: filtersSubscriber,
                isAllPlaceLanguage,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  getAllPlaceLanguageCompose: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ isAllPlaceLanguage = true }) =>
          backend
            .placeLanguageCompose(
              {
                placeId: getPlaceId(),
                status: filtersSubscriber,
                isAllPlaceLanguage,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  getLanguageBySubGroups: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({
          subgroups,
          subscribers,
          topicIds,
          channels,
          isAllPlaceLanguage = true,
        }) =>
          backend
            .placeLanguage(
              {
                placeId: getPlaceId(),
                subgroups,
                subscribers,
                status: filtersSubscriber,
                topicIds,
                channels,
                isAllPlaceLanguage,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  getLanguageBySubGroupsCompose: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({
          subgroups,
          subscribers,
          topicIds,
          channels,
          composeTitle,
          composeBody,
          composeDescription,
          voucherTitle,
          voucherDescription,
          composeQuestion,
          composeOptions,
          isAllPlaceLanguage = true,
        }) =>
          backend
            .placeLanguageCompose(
              {
                placeId: getPlaceId(),
                subgroups,
                subscribers,
                status: filtersSubscriber,
                topicIds,
                channels,
                composeTitle,
                composeBody,
                composeDescription,
                voucherTitle,
                voucherDescription,
                composeQuestion,
                composeOptions,
                isAllPlaceLanguage,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  subscriberNumber: withAuthHeaders(
    (authHeaders) =>
      ({ placeId }) =>
        backend.subscriberNumber({ placeId }, authHeaders).then(parseResponse),
  ),

  setupAccount: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .getPlaceCompletedSteps(requestData, authHeaders)
        .then(parseResponse),
  ),

  sendNotification: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .sendNotification(
            {
              placeId: getPlaceId(),
              data: notificationTranslationMapper.toAPI(data),
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  updateNotification: withAuthHeaders(
    (authHeaders) => (notificationId, notification) =>
      backend
        .updateNotification(
          {
            notificationId,
            notification: notificationTranslationMapper.toAPI(notification),
          },
          authHeaders,
        )
        .then(parseResponse),
  ),

  getNotificationById: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (notificationId) =>
        backend
          .getNotification(
            { placeId: getPlaceId(), notificationId },
            authHeaders,
          )
          .then(parseResponse)
          .then(notificationTranslationMapper.fromAPI),
    ),
  ),

  getSubscriberById: withAuthHeaders(
    (authHeaders) => (subscriberId) =>
      backend
        .getSubscriberById({ subscriberId }, authHeaders)
        .then(parseResponse),
  ),

  unsendNotification: withAuthHeaders(
    (authHeaders) => (notificationId) =>
      backend
        .unsendNotification(notificationId, authHeaders)
        .then(parseResponse),
  ),
  cancelEventNotification: withAuthHeaders(
    (authHeaders) => (notificationId, reason) =>
      backend
        .cancelEventNotification({ notificationId, reason }, authHeaders)
        .then(parseResponse),
  ),

  groupDetails: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend.groupDetails({ ...requestData }, authHeaders).then(parseResponse),
  ),
  groupsV4: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend.groupsV4({ ...requestData }, authHeaders).then(parseResponse),
  ),
  groupDetailV4: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (requestData) =>
        backend
          .groupDetailV4({ ...requestData, placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),

  addSubgroup: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend.addSubgroup({ ...requestData }, authHeaders).then(parseResponse),
  ),

  attachSubscribers: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .attachSubscribers({ ...requestData }, authHeaders)
        .then(parseResponse),
  ),

  addSubscriberToTags: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ tagId, subscriberIds }) =>
          backend
            .addSubscriberToTags(
              {
                placeId: getPlaceId(),
                tagId,
                subscriberIds,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  detachSubscribers: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .detachSubscribersSubgroups({ ...requestData }, authHeaders)
        .then(parseResponse),
  ),

  detachSubscribersFromGroup: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .detachSubscribersFromGroup({ ...requestData }, authHeaders)
        .then(parseResponse),
  ),

  updateGroup: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend.updateGroup({ ...requestData }, authHeaders).then(parseResponse),
  ),

  updateSubGroup: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .updateSubGroup({ ...requestData }, authHeaders)
        .then(parseResponse),
  ),

  deleteSubgroup: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .deleteSubgroup({ ...requestData }, authHeaders)
        .then(parseResponse),
  ),

  deleteGroups: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend.deleteGroups({ ...requestData }, authHeaders).then(parseResponse),
  ),

  createGroup: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend.createGroup({ ...requestData }, authHeaders).then(parseResponse),
  ),

  bulkAssignSubscribersToGroups: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        (requestData, customHeaders = {}) =>
          backend
            .bulkAssignSubscribersToGroups(
              { ...requestData, placeId: getPlaceId() },
              { ...authHeaders, ...customHeaders },
            )
            .then(parseResponse),
    ),
  ),

  getSubscribersList: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend.subscribers({ ...requestData }, authHeaders).then(parseResponse),
  ),

  createSubscriber: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .createSubscriber({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  updateSubscriber: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .updateSubscriber({ ...requestData }, authHeaders)
        .then(parseResponse),
  ),
  deleteSubscribersCRMCustomField: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .deleteSubscribersCRMCustomField({ ...requestData }, authHeaders)
        .then(parseResponse),
  ),
  getPlans: () => backend.getPlans().then(parseResponse),

  translate: withAuthHeaders(
    (authHeaders) => (data) =>
      backend.translate(data, authHeaders).then(parseResponse),
  ),

  interestBeacon: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .interestBeacon({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),

  deleteTeamMembers: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (ids) =>
        backend
          .deleteTeamMembers({ placeId: getPlaceId(), ids }, authHeaders)
          .then(parseResponse),
    ),
  ),

  checkValidYoutubeById: (videoId) =>
    backend.checkValidYoutubeById(videoId).then(parseResponse),

  getRandomPlaces: () => backend.getRandomPlaces().then(parseResponse),

  //TODO change to updateAdminPassword
  updateUserPassword: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .updateAdminPassword({ ...requestData }, authHeaders)
        .then(parseResponse),
  ),
  updateAdminProfile: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .updateAdminProfile({ ...requestData }, authHeaders)
        .then(parseResponse),
  ),

  updateInitialEmail: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ title, body }) =>
          backend
            .updateInitialEmail(
              { placeId: getPlaceId(), title, body },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  /*updateInitialEmail: withPlaceId(getPlaceId =>
        withAuthHeaders(authHeaders => data =>
          backend.updateInitialEmail({ placeId: getPlaceId(), ...data },
            authHeaders,
          ),
        ),
      ),*/

  updateInitialSMS: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ body }) =>
          backend
            .updateInitialSMS({ placeId: getPlaceId(), body }, authHeaders)
            .then(parseResponse),
    ),
  ),

  getInitialNotification: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getInitialNotification({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse)
          .then(notificationTranslationMapper.fromAPI),
    ),
  ),

  updateInitialNotification: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ data }) =>
          backend
            .updateInitialNotification(
              {
                placeId: getPlaceId(),
                data: notificationTranslationMapper.toAPI(data),
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  // createComment: withAuthHeaders((authHeaders) => (commentItem) => {
  //   return backend
  //     .createComment({ ...commentItem }, authHeaders)
  //     .then(parseResponse)
  // }),
  // updateComment: withAuthHeaders((authHeaders) => (updatedItem) => {
  //   return backend.updateComment(updatedItem, authHeaders).then(parseResponse)
  // }),

  approveSubscriber: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .approveSubscriber({ ...requestData }, authHeaders)
        .then(parseResponse),
  ),

  // deleteComment: withAuthHeaders((authHeaders) => (id) => {
  //   return backend.deleteComment(id, authHeaders).then(parseResponse)
  // }),

  getAllLanguage: withAuthHeaders(
    (authHeaders) => () =>
      backend
        .getLanguage({ page: 1, limit: 3000 }, authHeaders)
        .then(parseResponse),
  ),

  completeAccountSetupStep: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (stepId) =>
        backend
          .completeAccountSetupStep(
            { placeId: getPlaceId(), stepId },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  deleteSubscribers: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend.deleteSubscribers(requestData, authHeaders).then(parseResponse),
  ),

  checkImportProcess: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend.checkImportProcess(requestData, authHeaders).then(parseResponse),
  ),

  importCSVToLambda: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend.importCSVToLambda(requestData, authHeaders).then(parseResponse),
  ),

  checkCSVValidationToLambda: withAuthHeaders(
    (authHeaders) => (requestData) =>
      backend
        .checkCSVValidationToLambda(requestData, authHeaders)
        .then(parseResponse),
  ),

  cancelReminder: withAuthHeaders(
    (authHeaders) => (notiId) =>
      backend.cancelReminder(notiId, authHeaders).then(parseResponse),
  ),
  cancelScheduleNotification: withAuthHeaders(
    (authHeaders) => (notiId) =>
      backend
        .cancelSchduleNotification(notiId, authHeaders)
        .then(parseResponse),
  ),

  getAWSCognitoAuth: (accessToken) =>
    backend
      .getAWSCognitoAuth(accessTokenToAuthorizationHeaders(accessToken))
      .then(parseResponse),

  settings: withAuthHeaders(
    (authHeaders) => () => backend.settings(authHeaders).then(parseResponse),
  ),

  getNotiEventSubscribers: withAuthHeaders(
    (authHeaders) =>
      ({ notificationId, action, page, limit }) =>
        backend
          .getNotiEventSubscribers(
            { notificationId, action, page, limit },
            authHeaders,
          )
          .then(parseResponse),
  ),
  downloadEventNotification: withAuthHeaders(
    (authHeaders) =>
      ({ notificationId, action }) =>
        backend
          .downloadEventNotification({ notificationId, action }, authHeaders)
          .then(parseResponse),
  ),
  googleLogin: withAuthHeaders(
    (authHeaders) => () => backend.googleLogin(authHeaders).then(parseResponse),
  ),
  getGoogleContacts: withAuthHeaders(
    (authHeaders) =>
      ({ placeId, code }) =>
        backend
          .getGoogleContacts({ placeId, code }, authHeaders)
          .then(parseResponse),
  ),
  importGoogleContacts: withAuthHeaders(
    (authHeaders) =>
      ({ placeId, contacts, tagIds }) =>
        backend
          .importGoogleContacts({ placeId, contacts, tagIds }, authHeaders)
          .then(parseResponse),
  ),
  ttsSettings: withAuthHeaders(
    (authHeaders) => () => backend.ttsSettings(authHeaders).then(parseResponse),
  ),
  checkAudioExists: withAuthHeaders(
    (authHeaders) =>
      ({ notificationId, langId }) =>
        backend
          .checkAudioExists({ notificationId, langId }, authHeaders)
          .then(parseResponse),
  ),

  getConversationList: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ search, limit, lastKey, ...otherParam }) =>
          backend
            .getConversationList(
              {
                placeId: getPlaceId(),
                search,
                limit,
                last_key: lastKey,
                ...otherParam,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  createUserTransfer: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ inviteeUserId, password }) =>
          backend
            .createUserTransfer(
              { placeId: getPlaceId(), inviteeUserId, password },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  changeTransferStatus: withAuthHeaders(
    (authHeaders) =>
      ({ transferRequestId, status }) =>
        backend
          .changeTransferStatus({ transferRequestId, status }, authHeaders)
          .then(parseResponse),
  ),
  setTransferIsSeen: withAuthHeaders(
    (authHeaders) =>
      ({ transferRequestId }) =>
        backend
          .setTransferIsSeen({ transferRequestId }, authHeaders)
          .then(parseResponse),
  ),

  getBeacons: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .getBeacons({ placeId: getPlaceId(), ...data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  createBeacons: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .createBeacons({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  deleteBeacons: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .deleteBeacons(
            { placeId: getPlaceId(), data: { ids: data } },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  updateBeacons: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .updateBeacons({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  updateBeaconMetrics: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (beaconId) =>
        backend
          .updateBeaconMetrics({ placeId: getPlaceId(), beaconId }, authHeaders)
          .then(parseResponse),
    ),
  ),
  checkBeaconPhysicalAddress: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (physicalAddress) =>
        backend
          .checkBeaconPhysicalAddress(physicalAddress, authHeaders)
          .then(parseResponse),
    ),
  ),

  // getNotificationBeacons: withPlaceId((getPlaceId) =>
  //   withAuthHeaders(
  //     (authHeaders) => (data) =>
  //       backend
  //         .getNotifications({ placeId: getPlaceId(), data }, authHeaders)
  //         .then(parseResponse)
  //         .then(
  //           R.over(
  //             R.lensProp('data'),
  //             R.map(notificationTranslationMapper.fromAPI),
  //           ),
  //         ),
  //   ),
  // ),
  getNotificationBeacons: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ page, limit, sort, keyword }) =>
          backend
            .getNotificationBeacons(
              { placeId: getPlaceId(), page, limit, sort, keyword },
              authHeaders,
            )
            .then(parseResponse)
            .then(
              R.over(
                R.lensProp('data'),
                R.map(notificationTranslationMapper.fromAPI),
              ),
            ),
    ),
  ),

  getConversationDetail: withAuthHeaders(
    (authHeaders) => (historyId) =>
      backend.getConversationDetail(historyId, authHeaders).then(parseResponse),
  ),

  authConversation: (requestData) =>
    backend.authConversation(requestData).then(parseResponse),

  getConversationHistory: (
    token,
    history_id,
    limit = CONVERSATION_FETCH_SIZE,
    lastKey = '',
  ) =>
    backend
      .getConversationHistory(token, history_id, limit, lastKey)
      .then(parseResponse),

  createNotificationBeacon: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .createNotificationBeacon(
            {
              placeId: getPlaceId(),
              data: notificationTranslationMapper.toAPI(data),
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  updateNotificationBeacon: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (notificationId, data) =>
        backend
          .updateNotificationBeacon(
            {
              placeId: getPlaceId(),
              notificationId: notificationId,
              data: notificationTranslationMapper.toAPI(data),
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  deleteNotificationBeacons: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (notificationId) =>
        backend
          .deleteNotificationBeacons(
            { placeId: getPlaceId(), notificationId },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  updateStatusNotificationBeacon: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ notificationId, isActive }) =>
          backend
            .updateStatusNotificationBeacon(
              { placeId: getPlaceId(), notificationId, isActive },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  updateCareCheck: withAuthHeaders(
    (authHeaders) =>
      ({ placeId, shouldSeeCareCheck }) =>
        backend
          .updateCareCheck({ placeId, shouldSeeCareCheck }, authHeaders)
          .then(parseResponse),
  ),
  getSubscriber: withAuthHeaders(
    (authHeaders) =>
      ({ subscriberId }) =>
        backend
          .getSubscriber({ subscriberId }, authHeaders)
          .then(parseResponse),
  ),
  getVoucherInfo: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ voucherId, parentId }) =>
          backend
            .getVoucherInfo(
              { placeId: getPlaceId(), voucherId, parentId },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  approveRedemption: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ parentId, alertId, voucherId }) =>
          backend
            .approveRedemption(
              { placeId: getPlaceId(), parentId, alertId, voucherId },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  rejectRedemption: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ parentId, alertId, voucherId }) =>
          backend
            .rejectRedemption(
              { placeId: getPlaceId(), parentId, alertId, voucherId },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  groupAndIndividualSubscribersCount: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subgroupIds, subscriberIds }) =>
          backend
            .groupAndIndividualSubscribersCount(
              {
                placeId: getPlaceId(),
                subgroupIds,
                subscriberIds,
                status: filtersSubscriber,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getCuratedCheck: (googlePlaceId) =>
    backend.getCuratedCheck(googlePlaceId, {}).then(parseResponse),

  getGroupCategories: withAuthMAModeOnly(
    (authHeaders) => () =>
      backend.getGroupCategories(authHeaders).then(parseResponse),
  ),
  updatePlaceCategory: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ categoryId }) =>
          backend
            .updatePlaceCategory(
              {
                placeId: getPlaceId(),
                categoryId,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  findJob: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .findJob({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  getCountries: withAuthHeaders(
    (authHeaders) => () =>
      backend.getCountries(authHeaders).then(parseResponse),
  ),
  getNationalities: withAuthHeaders(
    (authHeaders) => () =>
      backend.getNationalities(authHeaders).then(parseResponse),
  ),
  getStatesByCountryId: withAuthHeaders(
    (authHeaders) => (data) =>
      backend.getStatesByCountryId(data, authHeaders).then(parseResponse),
  ),
  createNotes: withAuthHeaders(
    (authHeaders) => (data) =>
      backend.createNotes(data, authHeaders).then(parseResponse),
  ),
  updateNotes: withAuthHeaders(
    (authHeaders) => (data) =>
      backend.updateNotes(data, authHeaders).then(parseResponse),
  ),
  getNotesBySubscriberId: withAuthHeaders(
    (authHeaders) => (params) =>
      backend.getNotesBySubscriberId(params, authHeaders).then(parseResponse),
  ),
  deleteNoteById: withAuthHeaders(
    (authHeaders) => (data) =>
      backend.deleteNoteById(data, authHeaders).then(parseResponse),
  ),
  uploadImage: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ formData, onProgressBar, index }) =>
          backend
            .uploadImage(
              {
                placeId: getPlaceId(),
                data: formData,
                onProgressBar,
                index,
              },
              { ...authHeaders, 'Content-Type': 'multipart/form-data' },
            )
            .then(parseResponse),
    ),
  ),
  downloadImageFromUrl: ({ url }) => {
    return backend.downloadImageFromUrl({ url }).then((response) => {
      return response
    })
  },
  messageDismiss: withAuthHeaders(
    (authHeaders) => (data) =>
      backend.messageDismiss(data, authHeaders).then(parseResponse),
  ),

  allMessagesDismiss: withAuthHeaders(
    (authHeaders) => (data) =>
      backend.allMessagesDismiss(data, authHeaders).then(parseResponse),
  ),

  detectLanguage: withAuthHeaders(
    (authHeaders) => (data) =>
      backend.detectLanguage(data, authHeaders).then(parseResponse),
  ),

  getMemberCard: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .getMemberCard({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  validGroupForMemberCard: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .validGroupForMemberCard({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  updateMembershipCard: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .updateMembershipCard({ placeId: getPlaceId(), ...data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  createMembershipCard: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .createMembershipCard({ placeId: getPlaceId(), ...data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  getMemberCardOptions: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (subscriberId) =>
        backend
          .getMemberCardOptions(
            { placeId: getPlaceId(), subscriberId },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  getSubscriberMemberCard: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (subscriberId) =>
        backend
          .getSubscriberMemberCard(
            { placeId: getPlaceId(), subscriberId },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  createSubscriberMemberCard: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subscriberId, data }) =>
          backend
            .createSubscriberMemberCard(
              { placeId: getPlaceId(), subscriberId, data },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  updateSubscriberMemberCard: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subscriberId, data }) =>
          backend
            .updateSubscriberMemberCard(
              { placeId: getPlaceId(), subscriberId, data },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  deleteMembershipCard: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ cardId }) =>
          backend
            .deleteMembershipCard(
              { placeId: getPlaceId(), cardId },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getNotificationTimelineMetrics: withAuthHeaders(
    (authHeaders) =>
      ({ timeLineId, metricsType }) =>
        backend
          .getNotificationTimelineMetrics(
            { timeLineId, metricsType },
            authHeaders,
          )
          .then(parseResponse),
  ),
  getNotificationMetrics: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (notificationId) =>
        backend
          .getNotificationMetrics(
            { placeId: getPlaceId(), notificationId },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  getNotificationLanguage: withPlaceId((getPlaceById) =>
    withAuthHeaders(
      (authHeaders) => (notificationId) =>
        backend
          .getNotificationLanguage(
            { placeId: getPlaceById(), notificationId },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  getDealsPromotionList: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (props) =>
        backend
          .getDealsPromotionList(
            { placeId: getPlaceId(), ...props },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  getSubscribersExportData: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        (params = {}) =>
          backend
            .getSubscribersExportData(params, getPlaceId(), authHeaders)
            .then(parseResponse),
    ),
  ),
  createVoucher: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .createVoucher({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  updateVoucher: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (voucherId, data) =>
        backend
          .updateVoucher(
            { placeId: getPlaceId(), data, voucherId },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  conversationsArchive: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ placeId, historyId }) =>
          backend
            .conversationsArchive(
              { placeId: getPlaceId(), historyId },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  conversationsDelete: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ parentId, historyId }) =>
          backend
            .conversationsDelete(
              { placeId: getPlaceId(), historyId },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getVoucherTemplateList: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getVoucherTemplateList({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  cancelDealsPromotions: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (voucherId) =>
        backend
          .cancelDealsPromotions(
            { placeId: getPlaceId(), voucherId },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  createDraftNotification: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .createDraftNotification(
            {
              placeId: getPlaceId(),
              data: notificationTranslationMapper.toAPI(data),
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  sendDraftNotification: withPlaceId(() =>
    withAuthHeaders(
      (authHeaders) => (id, data) =>
        backend
          .sendDraftNotification(
            { id, data: notificationTranslationMapper.toAPI(data) },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  getDraftNotifications: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ page, limit, sort, keyword, type }) =>
          backend
            .getDraftNotifications(
              { placeId: getPlaceId(), page, limit, sort, keyword, type },
              authHeaders,
            )
            .then(parseResponse)
            .then(
              R.over(
                R.lensProp('data'),
                R.map(notificationTranslationMapper.fromAPI),
              ),
            ),
    ),
  ),
  getDraftNotificationsCount: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getDraftNotificationsCount({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  updateDraftNotification: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (requestedData) =>
        backend
          .updateDraftNotification(
            {
              placeId: getPlaceId(),
              ...R.over(
                R.lensProp('notification'),
                notificationTranslationMapper.toAPI,
                requestedData,
              ),
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  deleteDraftNotification: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (id) =>
        backend
          .deleteDraftNotification({ placeId: getPlaceId(), id }, authHeaders)
          .then(parseResponse),
    ),
  ),
  saveMessageAsDraft: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ data }) =>
          backend
            .saveMessageAsDraft(
              {
                placeId: getPlaceId(),
                data: notificationTranslationMapper.toAPI(data),
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  editDraftMessage: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ id, data }) =>
          backend
            .editDraftMessage(
              {
                placeId: getPlaceId(),
                id,
                data: notificationTranslationMapper.toAPI(data),
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getDraftMessage: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ id }) =>
          backend
            .getDraftMessage({ placeId: getPlaceId(), id }, authHeaders)
            .then(parseResponse),
    ),
  ),
  deleteDraftMessage: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (id) =>
        backend
          .deleteDraftMessage({ placeId: getPlaceId(), id }, authHeaders)
          .then(parseResponse),
    ),
  ),
  translateSubscriberFullName: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subscriberId, data }) =>
          backend
            .translateSubscriberFullName(
              { placeId: getPlaceId(), subscriberId, data },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  setTranslatedName: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subscriberId }) =>
          backend
            .setTranslatedName(
              { placeId: getPlaceId(), subscriberId },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getPlacePhotos: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getPlacePhotos({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  deletePhotos: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .deletePhotos({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  uploadPhotos: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data, onProgressBar, index) =>
        backend
          .uploadPhotos(
            { placeId: getPlaceId(), data, onProgressBar, index },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  openingHours: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .openingHours({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  saveOpeningHours: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .saveOpeningHours({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  getGenerateLandingUrl: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getGenerateLandingUrl({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  putUniqueName: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .putUniqueName({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  getEnvData: withAuthHeaders(
    (authHeaders) => () => backend.getEnvData(authHeaders).then(parseResponse),
  ),
  getVoucherView: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ html, voucherTemplateId, isStyle }) =>
          backend
            .getVoucherView(
              { html, voucherTemplateId, isStyle, placeId: getPlaceId() },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getSocialIntegrationSettings: withAuthHeaders(
    (authHeaders) =>
      ({ placeId }) =>
        backend
          .getSocialIntegrationSettings({ placeId }, authHeaders)
          .then(parseResponse),
  ),
  getSocialProviderPages: withAuthHeaders(
    (authHeaders) =>
      ({ placeId, socialProviderKey }) =>
        backend
          .getSocialProviderPages({ placeId, socialProviderKey }, authHeaders)
          .then(parseResponse),
  ),
  updateSocialPageConnectionStatus: withAuthHeaders(
    (authHeaders) =>
      ({ placeId, socialProviderKey, pageId, status }) =>
        backend
          .updateSocialPageConnectionStatus(
            { placeId, socialProviderKey, pageId, status },
            authHeaders,
          )
          .then(parseResponse),
  ),
  connectSocialProvider: withAuthHeaders(
    (authHeaders) =>
      ({ code, socialProviderKey, placeId }) =>
        backend
          .connectSocialProvider(
            { code, socialProviderKey, placeId },
            authHeaders,
          )
          .then(parseResponse),
  ),
  connectionSocialProviderTwitter: withAuthHeaders(
    (authHeaders) =>
      ({ oauthToken, oauthVerifier, type, placeId }) =>
        backend
          .connectSocialProviderTwitterConnect(
            { oauthToken, oauthVerifier, type, placeId },
            authHeaders,
          )
          .then(parseResponse),
  ),
  disconnectSocialProvider: withAuthHeaders(
    (authHeaders) =>
      ({ socialProviderKey, placeId }) =>
        backend
          .disconnectSocialProvider({ socialProviderKey, placeId }, authHeaders)
          .then(parseResponse),
  ),
  updateShowLanding: withAuthHeaders(
    (authHeaders) =>
      ({ showLanding, placeId }) =>
        backend
          .updateShowLanding({ showLanding, placeId }, authHeaders)
          .then(parseResponse),
  ),
  getSubscriberCount: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subgroupIds, subscriberIds, topicIds, scope }) =>
          backend
            .getSubscriberCount(
              {
                placeId: getPlaceId(),
                subgroupIds,
                subscriberIds,
                status: filtersSubscriber,
                topicIds,
                scope,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getSubscriberCountForReceive: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subgroupIds, subscriberIds, topicIds, channels }) =>
          backend
            .getSubscriberCountForReceive(
              {
                placeId: getPlaceId(),
                subgroupIds,
                subscriberIds,
                status: filtersSubscriber,
                topicIds,
                channels,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  createLanding: withAuthHeaders(
    (authHeaders) =>
      ({ placeId }) =>
        backend.createLanding({ placeId }, authHeaders).then(parseResponse),
  ),
  getRecipientCountForTimeline: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ notificationId }) =>
          backend
            .getRecipientCountForTimeline(
              {
                placeId: getPlaceId(),
                notificationId,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getPlacePlanInfo: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ cancelToken }) =>
          backend
            .getPlacePlanInfo(
              { placeId: getPlaceId(), cancelToken },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getPlaceLastNotification: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ cancelToken }) =>
          backend
            .getPlaceLastNotification(
              { placeId: getPlaceId(), cancelToken },
              authHeaders,
            )
            .then(parseResponse)
            .then(notificationTranslationMapper.fromAPI),
    ),
  ),
  getTopicList: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .getTopicList({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  createTopic: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .createTopic({ placeId: getPlaceId(), ...data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  updateTopic: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .updateTopic({ placeId: getPlaceId(), ...data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  deleteTopic: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ id }) =>
          backend
            .deleteTopic({ placeId: getPlaceId(), id }, authHeaders)
            .then(parseResponse),
    ),
  ),
  getSelectedTopics: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ notificationId }) =>
          backend
            .getSelectedTopics(
              { placeId: getPlaceId(), notificationId },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getPlaceSubscribersGrowth: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ cancelToken, period }) =>
          backend
            .getPlaceSubscribersGrowth(
              { placeId: getPlaceId(), cancelToken, period },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getNotificationSocialMediaInteractions: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ notificationId, cancelToken }) =>
          backend
            .getNotificationSocialMediaInteractions(
              { placeId: getPlaceId(), notificationId, cancelToken },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  hideCreditModal: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ status }) =>
          backend.hideCreditModal(
            { placeId: getPlaceId(), status },
            authHeaders,
          ),
    ),
  ),
  getUsageHistoryCount: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ date, entityType }) =>
          backend
            .getUsageHistoryCount(
              { placeId: getPlaceId(), date, entityType },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getAdvancedEditorUploadInfo: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getAdvancedEditorUploadInfo({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  getPublicNotificationLink: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ notificationId }) =>
          backend
            .getPublicNotificationLink(
              { placeId: getPlaceId(), notificationId },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getTranslationLanguageById: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ notificationId }) =>
          backend
            .getTranslationLanguageById(
              { placeId: getPlaceId(), notificationId },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  markAsCorrectSubscriber: withAuthHeaders(
    (authHeaders) => (params) =>
      backend.markAsCorrectSubscriber(params, authHeaders).then(parseResponse),
  ),

  phoneValidation: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ phoneNumber }) =>
          backend
            .phoneValidation(
              { placeId: getPlaceId(), phoneNumber },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  getSubscribersFollowTopics: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ topicsId, page, limit, sort }) =>
          backend
            .getSubscribersFollowTopics(
              { placeId: getPlaceId(), topicsId, page, limit, sort },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  getTagIds: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ groupIds }) =>
          backend
            .getTagIds({ placeId: getPlaceId(), groupIds }, authHeaders)
            .then(parseResponse),
    ),
  ),

  unfollowTopic: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ topicsId, subscriberIds }) =>
          backend
            .unfollowTopic(
              { placeId: getPlaceId(), topicsId, subscriberIds },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  updateDisplayHistory: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ notificationId, displayHistory }) =>
          backend
            .updateDisplayHistory(
              { placeId: getPlaceId(), notificationId, displayHistory },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getSubscribersTopics: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getSubscribersTopics({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  subscribersFollowTopics: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ topicsId, subscriberIds }) =>
          backend
            .subscribersFollowTopics(
              { placeId: getPlaceId(), topicsId, subscriberIds },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getTopicId: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ topicId }) =>
          backend
            .getTopicId({ placeId: getPlaceId(), topicId }, authHeaders)
            .then(parseResponse),
    ),
  ),
  cancelExpiration: withAuthHeaders(
    (authHeaders) => (notiId) =>
      backend.cancelExpiration(notiId, authHeaders).then(parseResponse),
  ),
  uploadImageFile: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ file }, onProgressBar) =>
          backend
            .uploadImageFile(
              {
                placeId: getPlaceId(),
                data: { file },
                onProgressBar,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  uploadImageFileForCrm: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ file, subscriberId }) =>
          backend
            .uploadImageFileForCrm(
              {
                placeId: getPlaceId(),
                subscriberId,
                data: { file },
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  autoApproval: withAuthHeaders(
    (authHeaders) =>
      ({ placeId, data }) =>
        backend
          .autoApproval({ placeId, data }, authHeaders)
          .then(parseResponse),
  ),
  getAutomatedCallSetting: withAuthHeaders(
    (authHeaders) =>
      ({ placeId }) =>
        backend
          .getAutomatedCallSetting({ placeId }, authHeaders)
          .then(parseResponse),
  ),
  updateAutomatedCallSetting: withAuthHeaders(
    (authHeaders) =>
      ({ placeId, data }) =>
        backend
          .updateAutomatedCallSetting({ placeId, data }, authHeaders)
          .then(parseResponse),
  ),
  getMessageRatio: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subgroupIds, subscriberIds, topicIds, channels }) =>
          backend
            .getMessageRatio(
              {
                placeId: getPlaceId(),
                subgroupIds,
                subscriberIds,
                status: filtersSubscriber,
                topicIds,
                channels,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getBillingPlan: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getBillingPlan({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  getBillingAccumulateUsage: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ startDate, endDate, showAll, type, timezone }) =>
          backend
            .getBillingAccumulateUsage(
              {
                placeId: getPlaceId(),
                startDate,
                endDate,
                showAll,
                type,
                timezone,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getBillingPayment: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getBillingPayment({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  getUsageLineChart: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ startDate, endDate, type, timezone }) =>
          backend
            .getUsageLineChart(
              { placeId: getPlaceId(), startDate, endDate, type, timezone },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  getUsageLogs: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .getUsageLogs({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  deactivate: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .deactivate({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),

  notificationsList: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .notificationsList({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  ai: {
    token: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ signal }) =>
            backend.ai
              .token({ placeId: getPlaceId() }, authHeaders, signal)
              .then(parseResponse),
      ),
    ),
    topics: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ query, signal }) =>
            backend.ai
              .topics({ placeId: getPlaceId(), query }, authHeaders, signal)
              .then(parseResponse),
      ),
    ),
    content: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ data, signal }) =>
            backend.ai
              .content({ placeId: getPlaceId(), data }, authHeaders, signal)
              .then(parseResponse),
      ),
    ),
    keywords: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ data, signal }) =>
            backend.ai
              .keywords({ placeId: getPlaceId(), data }, authHeaders, signal)
              .then(parseResponse),
      ),
    ),
    image: {
      generate: withPlaceId((getPlaceId) =>
        withAuthHeaders(
          (authHeaders) =>
            ({ data, signal }) =>
              backend.ai.image
                .generate({ placeId: getPlaceId(), data }, authHeaders, signal)
                .then(parseResponse),
        ),
      ),
      recent: withPlaceId((getPlaceId) =>
        withAuthHeaders(
          (authHeaders) =>
            ({ data, signal }) =>
              backend.ai.image
                .recent({ placeId: getPlaceId(), data }, authHeaders, signal)
                .then(parseResponse),
        ),
      ),
    },
    languages: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ signal }) =>
            backend.ai
              .languages({ placeId: getPlaceId() }, authHeaders, signal)
              .then(parseResponse),
      ),
    ),
    rateMessage: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ data, signal }) =>
            backend.ai
              .rateMessage({ placeId: getPlaceId(), data }, authHeaders, signal)
              .then(parseResponse),
      ),
    ),
  },
  notificationDetail: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ notiId, language }) =>
          backend
            .notificationDetail(
              { placeId: getPlaceId(), notiId, language },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  importCSV: {
    validate: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (data, controller) =>
          backend.importCSV
            .validate({ placeId: getPlaceId(), data, controller }, authHeaders)
            .then(parseResponse),
      ),
    ),
    singleValidate: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ subscriber, index, processId, errors, signal }) =>
            backend.importCSV
              .singleValidate(
                {
                  placeId: getPlaceId(),
                  subscriber,
                  index,
                  processId,
                  errors,
                  signal,
                },
                authHeaders,
              )
              .then(parseResponse),
      ),
    ),
    importSubscribers: withPlaceId((getPlaceId) =>
      withAuthHeaders((authHeaders) => async (data, controller) => {
        return backend.importCSV
          .importSubscribers(
            { placeId: getPlaceId(), data, controller },
            authHeaders,
          )
          .then(parseResponse)
      }),
    ),
    cancelImport: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ importId }) =>
            backend.importCSV
              .cancelImport({ importId, placeId: getPlaceId() }, authHeaders)
              .then(parseResponse),
      ),
    ),
    checkSubscribersImport: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.importCSV
            .checkSubscribersImport({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
    getSubscribersImportDetails: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.importCSV
            .getSubscribersImportDetails({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
    getSubscribersImportErrors: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ processId }) =>
            backend.importCSV
              .getSubscribersImportErrors(
                { placeId: getPlaceId(), processId },
                authHeaders,
              )
              .then(parseResponse),
      ),
    ),
  },
  importExternalSource: {
    getSISList: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.importExternalSource
            .getSISList({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
    importSubscribers: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (data) =>
          backend.importExternalSource
            .importSubscribers({ placeId: getPlaceId(), data }, authHeaders)
            .then(parseResponse),
      ),
    ),
    getSubscribersImportDetails: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.importExternalSource
            .getSubscribersImportDetails({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
    getSyncSetting: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.importExternalSource
            .getSyncSetting({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
    updateSyncSetting: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (data) =>
          backend.importExternalSource
            .updateSyncSetting({ placeId: getPlaceId(), data }, authHeaders)
            .then(parseResponse),
      ),
    ),
  },

  getDefaultInitailTemplate: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getDefaultInitailTemplate({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  notificationUnsend: withAuthHeaders(
    (authHeaders) => (notificationId) =>
      backend
        .notificationUnsend(notificationId, authHeaders)
        .then(parseResponse),
  ),
  notificationCancelEvent: withAuthHeaders(
    (authHeaders) => (notificationId, reason) =>
      backend
        .notificationCancelEvent({ notificationId, reason }, authHeaders)
        .then(parseResponse),
  ),

  activeTeamMembers: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (userIds) =>
        backend
          .activeTeamMembers(
            { placeId: getPlaceId(), userIds: userIds },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  resendInvitedTeamMembers: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (userIds) =>
        backend
          .resendInvitedTeamMembers(
            { placeId: getPlaceId(), userIds: userIds },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  suspendTeamMembers: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (userIds) =>
        backend
          .suspendTeamMembers(
            { placeId: getPlaceId(), userIds: userIds },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  pollByNotification: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (notificationId, pollId, languageId) =>
        backend
          .pollByNotification(
            {
              placeId: getPlaceId(),
              notificationId: notificationId,
              pollId: pollId,
              language: languageId,
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  pollVoter: withAuthHeaders(
    (authHeaders) => (pollId, optionId, languageId) =>
      backend
        .pollVoters(
          { pollId: pollId, optionId: optionId, language: languageId },
          authHeaders,
        )
        .then(parseResponse),
  ),

  endPoll: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (notificationId) =>
        backend
          .endPoll(
            {
              placeId: getPlaceId(),
              notificationId: notificationId,
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  credits: {
    get: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ signal }) =>
            backend.credits
              .get({ placeId: getPlaceId() }, authHeaders, signal)
              .then(parseResponse),
      ),
    ),
  },

  previewAiUsageHistory: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (requestId) =>
        backend
          .previewAiUsageHistory(
            {
              placeId: getPlaceId(),
              requestId: requestId,
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  getUploadImageConfig: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getUploadImageConfig({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  getUploadFileUrl: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => () =>
        backend
          .getUploadFileUrl({ placeId: getPlaceId() }, authHeaders)
          .then(parseResponse),
    ),
  ),
  uploadJsonToS3: withAuthHeaders(
    () =>
      ({ url, jsonObject, progressCallback, controller }) =>
        backend
          .uploadJsonToS3({ url, jsonObject, progressCallback, controller })
          .then(parseResponse),
  ),
  uploadImageWithUrlS3: withAuthHeaders(
    (authHeaders) => (url, file) =>
      backend
        .uploadImageWithUrlS3({ url, file }, authHeaders)
        .then(parseResponse),
  ),
  getAiUsageLogt: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend.aiUsageLog
          .getAiUsageLogt({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),
  getAiUsageDetail: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (requestId) =>
        backend.aiUsageLog
          .getAiUsageDetail(
            { placeId: getPlaceId(), requestId: requestId },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  getSubscriberByIdV2: withAuthHeaders(
    (authHeaders) =>
      ({ subscriberId }) =>
        backend
          .getSubscriberByIdV2({ subscriberId }, authHeaders)
          .then(parseResponse),
  ),
  getGroupForSubscriberAttach: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ type, limit, page, search }) =>
          backend
            .getGroupForSubscriberAttach(
              { placeId: getPlaceId(), type, limit, page, search },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  getTopicsForSubscriberAttach: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ limit, page, search }) =>
          backend
            .getTopicsForSubscriberAttach(
              { placeId: getPlaceId(), limit, page, search },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  attachTopicToSubscriber: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subscriberId, topicIds }) =>
          backend
            .attachTopicToSubscriber(
              { placeId: getPlaceId(), subscriberId, topicIds },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  attachGroupToSubscriber: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subscriberId, tagIds }) =>
          backend
            .attachGroupToSubscriber(
              { placeId: getPlaceId(), subscriberId, tagIds },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),
  updateProfileDetailOfSubscriber: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ subscriberId, info }) =>
          backend
            .updateProfileDetailOfSubscriber(
              { placeId: getPlaceId(), subscriberId, info },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  deleteSubscribersV2: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (requestData) =>
        backend
          .deleteSubscribersV2(getPlaceId(), requestData, authHeaders)
          .then(parseResponse),
    ),
  ),

  bulkDeleteSubscribers: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (requestData) =>
        backend
          .bulkDeleteSubscribers(
            { placeId: getPlaceId(), ...requestData },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  createSubscriberV2: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .addOneByOneSubscriber({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  inviteSubscribers: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .inviteSubscribers({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  exportSubscribers: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (data) =>
        backend
          .exportSubscribers({ placeId: getPlaceId(), data }, authHeaders)
          .then(parseResponse),
    ),
  ),

  getComments: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (notiId) =>
        backend
          .getComments({ placeId: getPlaceId(), notiId: notiId }, authHeaders)
          .then(parseResponse),
    ),
  ),

  addComment: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (notiId, payload) =>
        backend
          .addComment(
            { placeId: getPlaceId(), notiId: notiId, payload: payload },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  updateComment: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (notiId, payload, commentId) =>
        backend
          .updateComment(
            {
              placeId: getPlaceId(),
              notiId: notiId,
              payload: payload,
              commentId: commentId,
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),

  deleteComment: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) => (notiId, commentId) =>
        backend
          .deleteComment(
            {
              placeId: getPlaceId(),
              notiId: notiId,
              commentId: commentId,
            },
            authHeaders,
          )
          .then(parseResponse),
    ),
  ),
  getActivity: withPlaceId((getPlaceId) =>
    withAuthHeaders(
      (authHeaders) =>
        ({ params, subscriberId }) =>
          backend
            .getActivity(
              {
                placeId: getPlaceId(),
                subscriberId: subscriberId,
                page: params.currentPage,
                limit: params.perPage,
              },
              authHeaders,
            )
            .then(parseResponse),
    ),
  ),

  message: {
    metric: {
      count: withPlaceId((getPlaceId) =>
        withAuthHeaders(
          (authHeaders) =>
            ({ messageId }) =>
              backend.message.metric
                .count(
                  {
                    messageId,
                  },
                  authHeaders,
                )
                .then(parseResponse),
        ),
      ),

      recipientsByMetrics: withPlaceId((getPlaceId) =>
        withAuthHeaders(
          (authHeaders) =>
            ({
              messageId,
              metricsType,
              pageSize,
              pageAfter,
              messageChannels,
            }) =>
              backend.message.metric
                .recipientsByMetrics(
                  {
                    messageId,
                    metricsType,
                    pageSize,
                    pageAfter,
                    messageChannels,
                  },
                  authHeaders,
                )
                .then(parseResponse),
        ),
      ),
    },
  },
  getGeoInfo: withAuthHeaders(
    (authHeaders) => () => backend.getGeoInfo(authHeaders).then(parseResponse),
  ),
  authV2: {
    verifyEmail: (playload) =>
      backend.authV2.verifyEmail(playload).then(parseResponse),
    authVerify: (playload) =>
      backend.authV2.authVerify(playload).then(parseResponse),
    otpRequest: (playload) =>
      backend.authV2.otpRequest(playload).then(parseResponse),
    healtCheck: () => backend.authV2.healtCheck().then(parseResponse),
    forgotPassword: (playload) =>
      backend.authV2.forgotPassword(playload).then(parseResponse),
    resetPassword: (playload) =>
      backend.authV2.resetPassword(playload).then(parseResponse),
    registration: (playload) =>
      backend.authV2.registration(playload).then(parseResponse),
    provider: (playload) =>
      backend.authV2.provider(playload).then(parseResponse),
    setPassword: withAuthHeaders(
      (authHeaders) => (payload) =>
        backend.authV2.setPassword(payload, authHeaders).then(parseResponse),
    ),
    transferAccount: withAuthHeaders(
      (authHeaders) => (payload) =>
        backend.authV2
          .transferAccount(payload, authHeaders)
          .then(parseResponse),
    ),
    changePassword: withAuthHeaders(
      (authHeaders) => (payload) =>
        backend.authV2.changePassword(payload, authHeaders).then(parseResponse),
    ),
    voucher: {
      approve: withAuthHeaders(
        (authHeaders) => (payload) =>
          backend.authV2.voucher.approve(payload, authHeaders),
      ),
      reject: withAuthHeaders(
        (authHeaders) => (payload) =>
          backend.authV2.voucher.reject(payload, authHeaders),
      ),
    },
  },
  onboarding: {
    googleCurated: withAuthHeaders(
      (authHeaders) => (payload) =>
        backend.onboarding
          .googleCurated(payload, authHeaders)
          .then(parseResponse),
    ),
    checkGoogleCurated: withAuthHeaders(
      (authHeaders) => (payload) =>
        backend.onboarding
          .checkGoogleCurated(payload, authHeaders)
          .then(parseResponse),
    ),
    descriptionAI: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (payload) =>
          backend.onboarding
            .descriptionAI(
              { placeId: getPlaceId(), payload: payload },
              authHeaders,
            )
            .then(parseResponse),
      ),
    ),
    setupPlace: withAuthHeaders(
      (authHeaders) => (payload) =>
        backend.onboarding
          .setupPlace({ payload: payload }, authHeaders)
          .then(parseResponse),
    ),
  },
  profileInfo: {
    getPlaceInfo: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.profileInfo
            .getPlaceInfo({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
    updatePlaceInfo: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (payload) =>
          backend.profileInfo
            .updatePlaceInfo(
              { placeId: getPlaceId(), payload: payload },
              authHeaders,
            )
            .then(parseResponse),
      ),
    ),
    placePublic: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (payload) =>
          backend.profileInfo
            .placePublic(
              { placeId: getPlaceId(), payload: payload },
              authHeaders,
            )
            .then(parseResponse),
      ),
    ),
    getOpeningHours: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.profileInfo
            .getOpeningHours({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
    updateOpeningHours: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (payload) =>
          backend.profileInfo
            .updateOpeningHours(
              { placeId: getPlaceId(), payload: payload },
              authHeaders,
            )
            .then(parseResponse),
      ),
    ),
    getGallery: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.profileInfo
            .getGallery({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
    deletePhoto: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (payload, signal) =>
          backend.profileInfo
            .deletePhoto(
              { placeId: getPlaceId(), payload: payload, signal: signal },
              authHeaders,
            )
            .then(parseResponse),
      ),
    ),
    uploadPhoto: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (payload, onProgressBar, index, signal) =>
          backend.profileInfo
            .uploadPhoto(
              {
                placeId: getPlaceId(),
                payload: payload,
                onProgressBar,
                index,
                signal: signal,
              },
              authHeaders,
            )
            .then(parseResponse),
      ),
    ),
    getLandingPage: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.profileInfo
            .getLandingPage({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
    updateLandingPage: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (payload) =>
          backend.profileInfo
            .updateLandingPage(
              {
                placeId: getPlaceId(),
                payload: payload,
              },
              authHeaders,
            )
            .then(parseResponse),
      ),
    ),
    confirmNewPlaceEmail: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) =>
          ({ token }) =>
            backend.profileInfo
              .confirmNewPlaceEmail(
                { placeId: getPlaceId(), token: token },
                authHeaders,
              )
              .then(parseResponse),
      ),
    ),
  },
  favotites: {
    add: withAuthHeaders(
      (authHeaders) => (payload) =>
        backend.authV2.favotites.add(payload, authHeaders).then(parseResponse),
    ),
    remove: withAuthHeaders(
      (authHeaders) => (payload) =>
        backend.authV2.favotites
          .remove(payload, authHeaders)
          .then(parseResponse),
    ),
  },
  integration: {
    getSetting: withAuthHeaders(
      (authHeaders) =>
        ({ placeId }) =>
          backend.integration
            .getSetting({ placeId }, authHeaders)
            .then(parseResponse),
    ),
    line: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => (payload) =>
          backend.integration
            .line({ placeId: getPlaceId(), ...payload }, authHeaders)
            .then(parseResponse),
      ),
    ),
    lineDisconnect: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.integration
            .lineDisconnect({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
    getTwitterToken: withPlaceId((getPlaceId) =>
      withAuthHeaders(
        (authHeaders) => () =>
          backend.integration
            .getTwitterToken({ placeId: getPlaceId() }, authHeaders)
            .then(parseResponse),
      ),
    ),
  },
}
